import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { merge } from 'rxjs/internal/observable/merge';
import { of } from 'rxjs/internal/observable/of';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { map } from 'rxjs/internal/operators/map';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { tap } from 'rxjs/internal/operators/tap';
import { ReplaySubject } from 'rxjs/internal/ReplaySubject';
import { Subject } from 'rxjs/internal/Subject';
import { distinctUntilChanged, exhaustMap, take } from 'rxjs/operators';
import { IGroup } from 'src/app/accounts/group/group.interface';
import { ConfirmDailogueComponent } from 'src/app/shared/components/dailogue/confirm-dailogue.component';
import { Api } from 'src/app/shared/constants/api';
import { APP_ROUTES } from 'src/app/shared/constants/approutes';
import { KEYS } from 'src/app/shared/constants/keys';
import { Message } from 'src/app/shared/constants/message';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { DataService } from 'src/app/shared/services/data.service';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { UtilityService } from 'src/app/shared/services/utility.service';

@Component({
    selector: 'app-container-margin-form',
    templateUrl: './container-margin-form.component.html',
    styleUrls: ['./container-margin-form.component.less'],
    standalone: false
})
export class ContainerMarginFormComponent implements OnInit {

  // Matches backend DECIMAL(8,3): up to 5 digits before the decimal, up to 3 after.
  private static readonly MARGIN_PATTERN = /^\d{1,5}(\.\d{1,3})?$/;

  containerMarginForm: FormGroup;

  businessSegments: string[] = ['Ocean Freight', 'Air Freight', 'Land Freight'];

  defaultContainerTypes: string[] = ['20ft', '40ft'];
  standardMarginColumns: string[] = ['container', 'marginPercentage', 'minimumMargin'];
  exceptionColumns: string[] = ['port', 'country', 'margin20ft', 'margin40ft', 'deactivate', 'action'];
  exceptionDataSource = new MatTableDataSource<FormGroup>();

  destinationDD: any = [];

  filteredSegments: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  segments: any;
  segmentSearchCtrl = new FormControl('');
  portSearchCtrls: FormControl[] = [];
  filteredPorts: ReplaySubject<IGroup[]>[] = [];
  searching: boolean = false;

  exceptionPortFilter = new FormControl('');
  exceptionCountryFilter = new FormControl('');

  isEditMode: boolean = false;
  isEditedData: boolean = false;
  addPermission: boolean = false;
  editPermission: boolean = false;
  viewPermission: boolean = false;
  deletePermission: boolean = false;

  onDestroy = new Subject<void>();
  destinationCriteria: any;
  readonly today = new Date();

  constructor(
    private formBuilder: FormBuilder,
    private service: DataService,
    private logger: NGXLogger,
    private permissionService: PermissionService,
    private utilityService: UtilityService,
    private dailog: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.getAllPermission();
    this.getSegments();
    this.getDestination();
    this.setupExceptionFilters();
    this.activatedRoute.params.subscribe((params) => {
      const id = +params['id'];
      if (id) {
        this.isEditMode = true;
        this.id.setValue(id);
        this.getContainerMarginById(id);
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  initializeForm() {
    this.containerMarginForm = this.formBuilder.group({
      id: [null],
      refNo: [null],
      businessSegmentId: [null, Validators.required],
      wefDate: [null, Validators.required],
      standardMargins: this.formBuilder.array(
        this.defaultContainerTypes.map(type => this.createStandardMarginRow(type))
      ),
      exceptionMargins: this.formBuilder.array([this.createExceptionRow()])
    });
    const { ctrl, subject } = this.buildPortSearch();
    this.portSearchCtrls.push(ctrl);
    this.filteredPorts.push(subject);
    this.refreshTable();
  }

  private getContainerMarginById(id: number): void {
    const start = performance.now();
    this.service.get(`${Api.CONTAINER_MARGIN}/${id}`).subscribe((res: any) => {
      this.logger.info(`[getContainerMarginById] Response time: ${(performance.now() - start).toFixed(2)}ms`);
      if (res) {
        this.logger.info('Container Margin by id', res);

        // Rebuild exceptionMargins: patchValue only patches existing controls by index,
        // so rows beyond the initial single empty row are silently dropped.
        const exceptionArray = this.exceptionMargins;
        while (exceptionArray.length) {
          exceptionArray.removeAt(0);
        }
        this.portSearchCtrls = [];
        this.filteredPorts = [];
        const exceptions: any[] = res.exceptionMargins ?? [];
        exceptions.forEach((item: any) => {
          exceptionArray.push(this.createExceptionRow(item));
          const { ctrl, subject } = this.buildPortSearch();
          this.portSearchCtrls.push(ctrl);
          this.filteredPorts.push(subject);
        });
        if (!exceptionArray.length) {
          exceptionArray.push(this.createExceptionRow());
          const { ctrl, subject } = this.buildPortSearch();
          this.portSearchCtrls.push(ctrl);
          this.filteredPorts.push(subject);
        }
        this.refreshTable();

        // Patch scalar fields and standardMargins (FormArray size matches by design).
        if (res.standardMargins?.length) {
          res.standardMargins = this.defaultContainerTypes.map(type =>
            res.standardMargins.find((r: any) => r.containerType === type) ?? {}
          );
        }
        this.containerMarginForm.patchValue(res);

        // Collect portIds from exception rows, ensure those destinations are in
        // destinationDD (fetching missing ones if needed), then patch country fields.
        const portIds: number[] = exceptions
          .map((item: any) => item.portId)
          .filter((pid: any) => !!pid);
        this.ensureDestinationsLoaded(portIds);
      }
    });
  }

  private ensureDestinationsLoaded(portIds: number[]): void {
    // Wait for the initial getDestination() call to emit via the first row's ReplaySubject.
    this.filteredPorts[0].pipe(take(1)).subscribe(() => {
      const uniqueIds = [...new Set(portIds)];
      const missingIds = uniqueIds.filter(
        id => !this.destinationDD.some((d: any) => d.id === id)
      );

      if (!missingIds.length) {
        this.patchCountriesFromPort();
        return;
      }

      // Fetch only the missing ports in one call, sized exactly to avoid pagination cut-off.
      this.service.get(Api.DESTINATION_NAMES_FOR_DD, {
        ids: missingIds,
        page: 0,
        size: missingIds.length
      }).subscribe((res: any) => {
        if (res?.contents?.length) {
          res.contents.forEach((dest: any) => {
            if (!this.destinationDD.some((d: any) => d.id === dest.id)) {
              this.destinationDD.push(dest);
            }
          });
          this.filteredPorts.forEach(s => s.next([...this.destinationDD]));
        }
        this.patchCountriesFromPort();
      });
    });
  }

  private patchCountriesFromPort(): void {
    this.exceptionMargins.controls.forEach((ctrl) => {
      const portId = ctrl.get('portId')?.value;
      if (!portId) return;
      const destination = this.destinationDD.find((d: any) => d.id === portId);
      if (destination) {
        (ctrl as FormGroup).patchValue({
          countryId: destination.countryId,
          countryName: destination.countryName ?? '',
        });
      }
    });
  }

  getSegments() {
    let companyId = +localStorage.getItem(KEYS.COMPANY_ID);
    if (companyId) {
      this.service.get(Api.COMPANY_BUSINESS_SEGMENT_DD + '/' + companyId).forEach(res => {
        if (res) {
          this.logger.info("Business Segments", res);
          this.segments = res;
          // this.segments = res.filter(r => r.businessSegmentName === 'LCL Export' || r.businessSegmentName === 'Headload Export');
          this.filteredSegments.next(this.segments);
        }
      });
    }
  }

  filterSegments() {
    this.segmentSearchCtrl.valueChanges.pipe(
      tap(() => this.searching = true),
      debounceTime(350),
      takeUntil(this.onDestroy),
      map(search => {
        if (!this.segments?.length) { return []; }

        const searchSegment = (search || '').toLowerCase();

        if (!searchSegment) { return this.segments; }

        return this.segments.filter(segment =>
          segment.businessSegmentName?.toLowerCase().includes(searchSegment)
        );
      })
    )
      .subscribe({
        next: filtered => {
          this.searching = false;
          this.filteredSegments.next(filtered);
        },
        error: () => { this.searching = false; }
      });
  }

  getDestination() {
    this.service.get(Api.DESTINATION_NAMES_FOR_DD, this.destinationCriteria).subscribe(res => {
      this.logger.info("Destinations: ", res)
      if (res) {
        this.logger.info("Destination Values", res);
        this.destinationDD = res.contents;
        this.filteredPorts.forEach(s => s.next([...this.destinationDD]));
      }
    })
  }

  private buildPortSearch(): { ctrl: FormControl; subject: ReplaySubject<IGroup[]> } {
    const ctrl = new FormControl('');
    const subject = new ReplaySubject<IGroup[]>(1);
    subject.next([...this.destinationDD]);
    ctrl.valueChanges.pipe(
      tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(300),
      map(value => (value || '').trim()),
      distinctUntilChanged(),
      exhaustMap(value => {
        if (!value) {
          subject.next([...this.destinationDD]);
          return of([...this.destinationDD]);
        }
        return this.service.get(Api.DESTINATION_NAMES_FOR_DD, { name: value }).pipe(
          map(res => res.contents || []),
          tap(filteredResults => subject.next([...filteredResults]))
        );
      })
    ).subscribe(() => this.searching = false);
    return { ctrl, subject };
  }

  private setupExceptionFilters(): void {
    this.exceptionDataSource.filterPredicate = (row: FormGroup, filterJson: string) => {
      const f = JSON.parse(filterJson);
      const portId = row.get('portId')?.value;
      const destination = this.destinationDD.find((d: any) => d.id === portId);
      const portText = destination ? `${destination.name ?? ''} ${destination.code ?? ''}`.toLowerCase() : '';
      const matchPort = !f.port || portText.includes(f.port.toLowerCase());
      const countryName = (row.get('countryName')?.value || '').toLowerCase();
      const matchCountry = !f.country || countryName.includes(f.country.toLowerCase());
      return matchPort && matchCountry;
    };

    this.exceptionPortFilter.valueChanges.pipe(debounceTime(300), takeUntil(this.onDestroy))
      .subscribe(() => this.applyExceptionFilters());
    this.exceptionCountryFilter.valueChanges.pipe(debounceTime(300), takeUntil(this.onDestroy))
      .subscribe(() => this.applyExceptionFilters());
  }

  private applyExceptionFilters(): void {
    this.exceptionDataSource.filter = JSON.stringify({
      port: this.exceptionPortFilter.value || '',
      country: this.exceptionCountryFilter.value || ''
    });
  }

  setPortValueChangeEvent(portId: number, rowIndex: number): void {
    let destination: any;
    this.filteredPorts[rowIndex].pipe(take(1)).subscribe((portList: IGroup[]) => {
      destination = this.destinationDD.find((d: any) => d.id === portId) ||
        portList.find((d: any) => d.id === portId);
    });
    if (destination) {
      if (!this.destinationDD.some((d: any) => d.id === destination.id)) {
        this.destinationDD.push(destination);
      }
      const row = this.exceptionMargins.at(rowIndex) as FormGroup;
      row.patchValue({
        countryId: destination.countryId,
        countryName: destination.countryName ?? '',
      });
    }
  }

  createStandardMarginRow(containerType: string, data?: any): FormGroup {
    return this.formBuilder.group({
      containerType: [data?.containerType ?? containerType],
      marginPercentage: [data?.marginPercentage ?? null,
      [Validators.required, Validators.pattern(ContainerMarginFormComponent.MARGIN_PATTERN)]],
      minimumMargin: [data?.minimumMargin ?? null,
      [Validators.required, Validators.pattern(ContainerMarginFormComponent.MARGIN_PATTERN)]],
    });
  }

  refreshTable(): void {
    this.exceptionDataSource.data = this.exceptionMargins.controls as FormGroup[];
  }

  addException(): void {
    const { ctrl, subject } = this.buildPortSearch();
    this.portSearchCtrls.push(ctrl);
    this.filteredPorts.push(subject);
    this.exceptionMargins.push(this.createExceptionRow());
    this.refreshTable();
  }

  createExceptionRow(data?: any): FormGroup {
    const formValue = this.formBuilder.group({
      portId: [data?.portId ?? null],
      countryId: [data?.countryId ?? null],
      countryName: [data?.countryName ?? null],
      margin20ft: [data?.margin20ft ?? null,
      [Validators.pattern(ContainerMarginFormComponent.MARGIN_PATTERN)]],
      margin40ft: [data?.margin40ft ?? null,
      [ Validators.pattern(ContainerMarginFormComponent.MARGIN_PATTERN)]],
      // minimumCbm: [data?.minimumCbm ?? null, Validators.min(1)],
      isDeactivated: [data?.isDeactivated ?? false],
    });
    this.wireExceptionRowValidators(formValue);
    return formValue;
  }

  // Exception rows are optional as a whole. Once any of portId/margin20ft/margin40ft
  // is filled in, the row is considered "active" and those three fields become required.
  private wireExceptionRowValidators(row: FormGroup): void {
    const portCtrl = row.get('portId');
    const margin20Ctrl = row.get('margin20ft');
    const margin40Ctrl = row.get('margin40ft');

    const recompute = () => {
      const isRowActive = this.hasValue(portCtrl.value) || this.hasValue(margin20Ctrl.value) || this.hasValue(margin40Ctrl.value);

      portCtrl.setValidators(isRowActive ? [Validators.required] : []);
      margin20Ctrl.setValidators(isRowActive
        ? [Validators.required, Validators.pattern(ContainerMarginFormComponent.MARGIN_PATTERN)]
        : [Validators.pattern(ContainerMarginFormComponent.MARGIN_PATTERN)]);
      margin40Ctrl.setValidators(isRowActive
        ? [Validators.required, Validators.pattern(ContainerMarginFormComponent.MARGIN_PATTERN)]
        : [Validators.pattern(ContainerMarginFormComponent.MARGIN_PATTERN)]);

      portCtrl.updateValueAndValidity({ emitEvent: false });
      margin20Ctrl.updateValueAndValidity({ emitEvent: false });
      margin40Ctrl.updateValueAndValidity({ emitEvent: false });
    };

    merge(portCtrl.valueChanges, margin20Ctrl.valueChanges, margin40Ctrl.valueChanges)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => recompute());

    recompute();
  }

  private hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  removeExceptionRow(index: number): void {
    this.exceptionMargins.removeAt(index);
    this.portSearchCtrls.splice(index, 1);
    this.filteredPorts.splice(index, 1);
    this.containerMarginForm.markAsDirty();
    if (this.exceptionMargins.length === 0) {
      this.addException();
    } else {
      this.refreshTable();
    }
  }

  onSave(): void {
    if (this.isEditMode && this.containerMarginForm.pristine) {
      this.utilityService.toastrInfoMessage('No changes to save');
      return;
    }
    this.containerMarginForm.markAllAsTouched();
    if (this.containerMarginForm.invalid) {
      this.showInvalidFieldsWarning();
      return;
    }
    this.isEditedData = true;
    this.openDialog(this.id.value ? Message.SAVE_OPEN_DAILOG_IN_EDIT : Message.SAVE_OPEN_DAILOG);
  }

  private showInvalidFieldsWarning(): void {
    const missing: string[] = [];

    const topLevel: Record<string, string> = {
      businessSegmentId: 'Business Segment',
      wefDate: 'WEF Date',
    };
    Object.entries(topLevel).forEach(([key, label]) => {
      if (this.containerMarginForm.get(key)?.invalid) { missing.push(label); }
    });

    const standardFields: Record<string, string> = {
      marginPercentage: 'Margin %',
      minimumMargin: 'Minimum Margin',
    };
    this.standardMargins.controls.forEach((row) => {
      const containerType = row.get('containerType')?.value ?? '';
      const rowMissing = Object.entries(standardFields)
        .filter(([key]) => row.get(key)?.invalid)
        .map(([, label]) => label);
      if (rowMissing.length) {
        missing.push(`Standard Margin (${containerType}): ${rowMissing.join(', ')}`);
      }
    });

    const exceptionFields: Record<string, string> = {
      portId: 'Port',
      margin20ft: '20ft Margin',
      margin40ft: '40ft Margin',
    };
    this.exceptionMargins.controls.forEach((row, i) => {
      const rowMissing = Object.entries(exceptionFields)
        .filter(([key]) => row.get(key)?.invalid)
        .map(([, label]) => label);
      if (rowMissing.length) {
        missing.push(`Exception Row ${i + 1}: ${rowMissing.join(', ')}`);
      }
    });

    this.utilityService.toastrWarningMessage(
      `Please enter valid values for — ${missing.join(' | ')}`
    );
  }

  private saveContainerMargin(): void {
    const isEdit = !!this.id.value;
    const method = isEdit ? 'put' : 'post';
    const successLabel = isEdit ? 'updated' : 'saved';

    const payload = this.containerMarginForm.getRawValue();
    if (payload.wefDate) {
      payload.wefDate = this.formatToISODate(payload.wefDate);
    }

    this.service[method](Api.CONTAINER_MARGIN, payload).subscribe({
      next: (res: any) => {
        this.logger.info('Container Margin saved', res);
        this.utilityService.notifier(`Container Margin ${successLabel} successfully`, 'success');
        this.router.navigate([APP_ROUTES.CONTAINER_MARGIN]);
      }
    });
  }

  onCancel(): void {
    if (this.containerMarginForm.dirty) {
      this.isEditedData = false;
      this.openDialog(this.id.value ? Message.CANCEL_OPEN_DAILOG_IN_EDIT : Message.CANCEL_OPEN_DAILOG);
    } else {
      this.router.navigate([APP_ROUTES.CONTAINER_MARGIN]);
    }
  }

  private openDialog(message: string): void {
    const dialogRef = this.dailog.open(ConfirmDailogueComponent, {
      data: message,
      maxWidth: '300px',
      maxHeight: '150px'
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result === false) return;

      if (this.isEditedData) {
        this.saveContainerMargin();
      } else {
        this.router.navigate([APP_ROUTES.CONTAINER_MARGIN]);
      }
    });
  }

  private formatToISODate(date: any): string | null {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  getAllPermission(): void {
    const permissionCodes = [PermissionKey.CONTAINER_MARGIN_ADD, PermissionKey.CONTAINER_MARGIN_EDIT, PermissionKey.CONTAINER_MARGIN_VIEW, PermissionKey.CONTAINER_MARGIN_DELETE];
    const permissions = this.permissionService.checkPermissions(permissionCodes, PermissionKey.CONTAINER_MARGIN);
    this.addPermission = permissions.find(p => p.permissionCode === PermissionKey.CONTAINER_MARGIN_ADD)?.show ?? false;
    this.editPermission = permissions.find(p => p.permissionCode === PermissionKey.CONTAINER_MARGIN_EDIT)?.show ?? false;
    this.viewPermission = permissions.find(p => p.permissionCode === PermissionKey.CONTAINER_MARGIN_VIEW)?.show ?? false;
    this.deletePermission = permissions.find(p => p.permissionCode === PermissionKey.CONTAINER_MARGIN_DELETE)?.show ?? false;
  }

  restrictDecimalInput(event: KeyboardEvent, decimalPlaces: number): void {
    const input = event.target as HTMLInputElement;
    const { key } = event;

    if (['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(key)) { return; }

    if (!/[\d.]/.test(key) || (key === '.' && input.value.includes('.'))) {
      event.preventDefault();
      return;
    }
    const cursorPosition = input.selectionStart || 0;
    const [integerPart = '', decimalPart = ''] = input.value.split('.');
    const decimalStart = integerPart.length;

    if (key !== '.' && cursorPosition <= decimalStart && integerPart.length >= 5) {
      event.preventDefault();
      return;
    }

    if (
      input.value.includes('.') &&
      cursorPosition > decimalStart &&
      decimalPart.length >= decimalPlaces &&
      key !== 'Backspace'
    ) {
      event.preventDefault();
    }
  }

  get id() { return this.containerMarginForm.get('id'); }
  get standardMargins(): FormArray { return this.containerMarginForm.get('standardMargins') as FormArray; }
  get exceptionMargins(): FormArray { return this.containerMarginForm.get('exceptionMargins') as FormArray; }
}
